import { createTheme } from '@mui/material/styles';
import { defaultTheme } from 'react-admin';
import { alpha } from '@mui/material/styles';

import variables from './variables';

const theme = {
	...defaultTheme,
	components: {
		...defaultTheme.components,
		MuiAppBar: {
			styleOverrides: {
				root: {
					boxShadow: '0px 1px 30px 1px rgba(0, 0, 0, 0.11)',
					zIndex: 1300
				}
			}
		},
		MuiDrawer: {
			styleOverrides: {
				docked: {
					marginRight: '19px',
					boxShadow: 'none !important',
					'& .footer-menu': {
						display: 'none'
					},
					'& .RaMenu-open': {
						height: 'calc(100% - 48px)',
						display: 'flex',
						flexDirection: 'column',
						'& .footer-menu': {
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'center',
							margin: '5px',
							justifySelf: 'flex-end',
							alignSelf: 'center',
							marginTop: 'auto'
						}
					}
				}
			}
		},
		RaSidebar: {
			styleOverrides: {
				root: {
					'& .RaSidebar-fixed': {
						boxShadow: 'none !important'
					}
				}
			}
		},
		MuiFormControl: {
			defaultProps: {
				variant: 'outlined',
				size: 'small'
			},
			styleOverrides: {
				root: {
					'.RaCreate-main &, .RaEdit-main &': {
						minWidth: '250px !important'
					}
				},
				fullWidth: {
					maxWidth: '50%',
					'@media (max-width: 600px)': {
						maxWidth: '100%'
					}
				}
			}
		},
		MuiAutocomplete: {
			styleOverrides: {
				root: {
					'.RaCreate-main &, .RaEdit-main &': {
						minWidth: '250px !important'
					}
				}
			}
		},
		RaSelectInput: {
			styleOverrides: {
				root: {
					'.RaCreate-main &, .RaEdit-main &': {
						minWidth: '250px !important'
					}
				}
			}
		},
		MuiTextField: {
			defaultProps: {
				variant: 'outlined',
				size: 'small'
			},
		},
		MuiSnackbarContent: {
			styleOverrides: {
				message: {
					whiteSpace: 'pre-wrap'
				}
			}
		},
		MuiMenuItem: {
			styleOverrides: {
				root: {
					'@media (min-width: 600px)': {
						minHeight: '36px'
					}
				}
			}
		},
		MuiInputBase: {
			defaultProps: {
				autoComplete: 'off'
			}
		},
		MuiCardHeader: {
			styleOverrides: {
				root: {
					borderBottom: '1px solid #DADCE0',
				},
				title: {
					fontSize: '22px'
				}
			}
		},
		RaRichTextInput: {
			styleOverrides: {
				root: {
					maxWidth: '100%',
					'& .RaRichTextInput-editorContent': {
						'& > div': {
							minHeight: '200px',
						}
					},
					'& .RaRichTextInput-editorContent img': {
						maxWidth: '100%'
					},
					'& .RaRichTextInputToolbar-root': {
						alignSelf: 'flex-start'
					},
					'& .ProseMirror': {
						borderColor: '#000 !important'
					},
					'& .tiptap': {
						'ul[data-type="taskList"]': {
							listStyle: 'none',
							marginLeft: 0,
							padding: 0,

							'li': {
								alignItems: 'flex-start',
								display: 'flex',

								'> label': {
									flex: '0 0 auto',
									marginRight: '0.5rem',
									userSelect: 'none'
								},

								'> div': {
									flex: '1 1 auto'
								}
							},

							'input[type="checkbox"]': {
								cursor: 'pointer'
							},

							'ul[data-type="taskList"]': {
								margin: 0
							}
						}
					}
				}
			}
		},
		MuiTypography: {
			styleOverrides: {
				root: {
					'& ul[data-type="taskList"]': {
						listStyle: 'none',
						marginLeft: 0,
						padding: 0,
						pointerEvents: 'none',

						'li': {
							alignItems: 'flex-start',
							display: 'flex',

							'> label': {
								flex: '0 0 auto',
								marginRight: '0.5rem',
								userSelect: 'none'
							},

							'> div': {
								flex: '1 1 auto'
							},
							'& p': {
								margin: 0
							}
						},

						'input[type="checkbox"]': {
							cursor: 'pointer'
						},

						'ul[data-type="taskList"]': {
							margin: 0
						}
					}
				}
			}
		},
		MuiButton: {
			styleOverrides: {
				contained: {
					boxShadow: 'none',
					'&:hover': {
						boxShadow: 'none'
					}
				}
			}
		},
		RaDatagrid: {
			styleOverrides: {
				root: {
					'& tbody .MuiTableRow-root:last-child .MuiTableCell-root': {
						border: 0
					}
				}
			}
		},
		MuiCssBaseline: {
			styleOverrides: {
				'.rich-text': {
					'& p:empty': {
						height: '1em'
					},
					'& img': {
						maxWidth: '100%'
					}
				}
			}
		},
		RaInPlaceEditor: {
			styleOverrides: {
				root: {
					minHeight: '20px',
					'& .RaInPlaceEditor-reading': {
						minHeight: 'inherit'
					},
					'& .RaInPlaceEditor-reading div, & .RaInPlaceEditor-editing input, & .RaInPlaceEditor-editing textarea, & .RaInPlaceEditor-saving div': {
						fontSize: '0.875rem',
						marginBottom: 0
					}
				}
			}
		}
	}
};

const lightTheme = createTheme({
	palette: {
		...theme.palette,
		primary: {
			main: variables.colors.primary
		}
	},
	shadows: [
		"none",
		"0 1px 15px 1px rgba(0, 0, 0, 0.11)",
		"0px 3px 1px -2px rgba(0,0,0,0.2),0px 2px 2px 0px rgba(0,0,0,0.14),0px 1px 5px 0px rgba(0,0,0,0.12)",
		"0px 3px 3px -2px rgba(0,0,0,0.2),0px 3px 4px 0px rgba(0,0,0,0.14),0px 1px 8px 0px rgba(0,0,0,0.12)",
		"0px 2px 4px -1px rgba(0,0,0,0.2),0px 4px 5px 0px rgba(0,0,0,0.14),0px 1px 10px 0px rgba(0,0,0,0.12)",
		"0px 3px 5px -1px rgba(0,0,0,0.2),0px 5px 8px 0px rgba(0,0,0,0.14),0px 1px 14px 0px rgba(0,0,0,0.12)",
		"0px 3px 5px -1px rgba(0,0,0,0.2),0px 6px 10px 0px rgba(0,0,0,0.14),0px 1px 18px 0px rgba(0,0,0,0.12)",
		"0px 4px 5px -2px rgba(0,0,0,0.2),0px 7px 10px 1px rgba(0,0,0,0.14),0px 2px 16px 1px rgba(0,0,0,0.12)",
		"0px 5px 5px -3px rgba(0,0,0,0.2),0px 8px 10px 1px rgba(0,0,0,0.14),0px 3px 14px 2px rgba(0,0,0,0.12)",
		"0px 5px 6px -3px rgba(0,0,0,0.2),0px 9px 12px 1px rgba(0,0,0,0.14),0px 3px 16px 2px rgba(0,0,0,0.12)",
		"0px 6px 6px -3px rgba(0,0,0,0.2),0px 10px 14px 1px rgba(0,0,0,0.14),0px 4px 18px 3px rgba(0,0,0,0.12)",
		"0px 6px 7px -4px rgba(0,0,0,0.2),0px 11px 15px 1px rgba(0,0,0,0.14),0px 4px 20px 3px rgba(0,0,0,0.12)",
		"0px 7px 8px -4px rgba(0,0,0,0.2),0px 12px 17px 2px rgba(0,0,0,0.14),0px 5px 22px 4px rgba(0,0,0,0.12)",
		"0px 7px 8px -4px rgba(0,0,0,0.2),0px 13px 19px 2px rgba(0,0,0,0.14),0px 5px 24px 4px rgba(0,0,0,0.12)",
		"0px 7px 9px -4px rgba(0,0,0,0.2),0px 14px 21px 2px rgba(0,0,0,0.14),0px 5px 26px 4px rgba(0,0,0,0.12)",
		"0px 8px 9px -5px rgba(0,0,0,0.2),0px 15px 22px 2px rgba(0,0,0,0.14),0px 6px 28px 5px rgba(0,0,0,0.12)",
		"0px 8px 10px -5px rgba(0,0,0,0.2),0px 16px 24px 2px rgba(0,0,0,0.14),0px 6px 30px 5px rgba(0,0,0,0.12)",
		"0px 8px 11px -5px rgba(0,0,0,0.2),0px 17px 26px 2px rgba(0,0,0,0.14),0px 6px 32px 5px rgba(0,0,0,0.12)",
		"0px 9px 11px -5px rgba(0,0,0,0.2),0px 18px 28px 2px rgba(0,0,0,0.14),0px 7px 34px 6px rgba(0,0,0,0.12)",
		"0px 9px 12px -6px rgba(0,0,0,0.2),0px 19px 29px 2px rgba(0,0,0,0.14),0px 7px 36px 6px rgba(0,0,0,0.12)",
		"0px 10px 13px -6px rgba(0,0,0,0.2),0px 20px 31px 3px rgba(0,0,0,0.14),0px 8px 38px 7px rgba(0,0,0,0.12)",
		"0px 10px 13px -6px rgba(0,0,0,0.2),0px 21px 33px 3px rgba(0,0,0,0.14),0px 8px 40px 7px rgba(0,0,0,0.12)",
		"0px 10px 14px -6px rgba(0,0,0,0.2),0px 22px 35px 3px rgba(0,0,0,0.14),0px 8px 42px 7px rgba(0,0,0,0.12)",
		"0px 11px 14px -7px rgba(0,0,0,0.2),0px 23px 36px 3px rgba(0,0,0,0.14),0px 9px 44px 8px rgba(0,0,0,0.12)",
		"0px 11px 15px -7px rgba(0,0,0,0.2),0px 24px 38px 3px rgba(0,0,0,0.14),0px 9px 46px 8px rgba(0,0,0,0.12)"
	],
	components: {
		...theme.components,
		RaLayout: {
			styleOverrides: {
				root: {
					backgroundColor: alpha(variables.colors.primary, 0.025),
					'& .RaLayout-content': {
						backgroundColor: 'transparent'
					}
				},
				content: {
					backgroundColor: 'transparent'
				}
			}
		},
		MuiPaper: {
			styleOverrides: {
				elevation1: {
					boxShadow: 'none',
					border: '1px solid #E0E0E0'
				}
			}
		},
		RaSidebar: {
			styleOverrides: {
				root: {
					'& .RaSidebar-paperAnchorLeft .RaSidebar-fixed': {
						borderRight: '1px solid #E0E0E0 !important'
					}
				}
			}
		},
	}
}, theme);

const defaultDarkTheme = createTheme({ palette: { mode: 'dark' } });
const darkTheme = createTheme({
	...theme,
	palette: {
		mode: 'dark',
		primary: {
			main: variables.colors.lightPrimary
		},
		background: {
			paper: '#262626'
		}
	},
	components: {
		...theme.components,
		MuiFormGroup: {
			styleOverrides: {
				root: {
					color: defaultDarkTheme.palette.text.primary
				}
			}
		},
		MuiFormControl: {
			styleOverrides: {
				root: {
					'& ::-webkit-calendar-picker-indicator': {
						filter: 'invert(1)',
						colorScheme: 'light'
					}
				}
			}
		},
		MuiCardHeader: {
			styleOverrides: {
				root: {
					borderBottom: `1px solid ${defaultDarkTheme.palette.background.default}`,
				},
				title: {
					fontSize: '22px'
				}
			}
		},
		MuiPaper: {
			styleOverrides: {
				elevation1: {
					border: '1px solid #535353 !important'
				}
			}
		},
	}
});

export { lightTheme, darkTheme, theme };
